import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  API_get_schedule,
  API_get_actual_used_schedule,
  API_get_role_types,
  API_get_actual_sales_daily,
  API_get_forecasted_sales_daily,
  API_get_effective_wages,
} from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../../../utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../dateRangePresets";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import {
  displayName,
  formatCurrency,
  formatHours,
  isNoShowActualShift,
  shiftDurationHours,
  unwrapPayload,
  lookupEffectiveWageOnDate,
} from "./_shared/laborMath";

// Local helpers for endpoints whose URL has "?store_id=" baked in.
function fetchStoreScoped(api, storeId, extraParams = "") {
  const url = `${api}${storeId}${extraParams ? "&" + extraParams : ""}`;
  return axios.get(url, getDefaultAxiosConfig());
}
function fetchStoreScopedRange(api, storeId, startStr, endStr) {
  return fetchStoreScoped(
    api,
    storeId,
    `date_start=${startStr}&date_end=${endStr}`
  );
}

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function emptyBucket() {
  return {
    scheduled: { shifts: 0, hours: 0, wages: 0 },
    actual: { shifts: 0, hours: 0, wages: 0 },
  };
}

function rate(wages, hours) {
  if (!hours || hours <= 0) return null;
  return wages / hours;
}

function laborPct(wages, sales) {
  if (!sales || sales <= 0) return null;
  return (wages / sales) * 100;
}

// Build a date → value map from a list of {date, value} records.
function indexByDate(records) {
  const m = new Map();
  if (!Array.isArray(records)) return m;
  for (const r of records) {
    if (r?.date != null) m.set(r.date, r.value);
  }
  return m;
}

function SectionCard({ title, footnote, children }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2">
        <h5 className="text-sm font-semibold text-slate-800">{title}</h5>
        {footnote && (
          <p className="text-[10px] text-slate-500 mt-0.5">{footnote}</p>
        )}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

const TH = ({ children, align = "left", colSpan, rowSpan, className = "" }) => (
  <th
    colSpan={colSpan}
    rowSpan={rowSpan}
    className={`font-semibold text-slate-700 px-3 py-2 text-${align} ${className}`}
  >
    {children}
  </th>
);
const TD = ({ children, align = "left", strong = false, className = "" }) => (
  <td
    className={`px-3 py-2 text-${align} ${
      strong ? "font-semibold text-slate-900" : "text-slate-700"
    } ${className}`}
  >
    {children}
  </td>
);

function SummaryTable({ rows, labelHeader }) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-white border-b border-slate-100">
        <tr>
          <TH>{labelHeader}</TH>
          <TH align="right">Sched Shifts</TH>
          <TH align="right">Act Shifts</TH>
          <TH align="right">Sched Hrs</TH>
          <TH align="right">Act Hrs</TH>
          <TH align="right">Est Rate</TH>
          <TH align="right">Act Rate</TH>
          <TH align="right">Proj Wages</TH>
          <TH align="right">Act Wages</TH>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-slate-50 hover:bg-slate-50">
            <TD strong>{r.label}</TD>
            <TD align="right">{r.scheduled.shifts}</TD>
            <TD align="right">{r.actual.shifts}</TD>
            <TD align="right">{formatHours(r.scheduled.hours)}</TD>
            <TD align="right">{formatHours(r.actual.hours)}</TD>
            <TD align="right">
              {rate(r.scheduled.wages, r.scheduled.hours) == null
                ? "—"
                : formatCurrency(rate(r.scheduled.wages, r.scheduled.hours))}
            </TD>
            <TD align="right">
              {rate(r.actual.wages, r.actual.hours) == null
                ? "—"
                : formatCurrency(rate(r.actual.wages, r.actual.hours))}
            </TD>
            <TD align="right">{formatCurrency(r.scheduled.wages)}</TD>
            <TD align="right">{formatCurrency(r.actual.wages)}</TD>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function WeeklyLaborScheduleSummaryReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
}) {
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, homeStore),
    [storeNames, homeStore]
  );

  const [weekStart, setWeekStart] = useState(() =>
    snapToWeekStart(new Date(), weekStartDayIndex)
  );

  useEffect(() => {
    setWeekStart((prev) => snapToWeekStart(prev, weekStartDayIndex));
  }, [weekStartDayIndex]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + 6);
    return d;
  }, [weekStart]);

  const incrementWeek = useCallback((currentStart, direction) => {
    const next = new Date(currentStart);
    next.setDate(next.getDate() + direction * 7);
    setWeekStart(next);
  }, []);

  const setWeekStartDate = useCallback(
    (newDate) => {
      setWeekStart(snapToWeekStart(newDate, weekStartDayIndex));
    },
    [weekStartDayIndex]
  );

  const weekDates = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
      }),
    [weekStart]
  );

  const startStr = useMemo(
    () => dayjs(weekStart).format("YYYY-MM-DD"),
    [weekStart]
  );
  const endStr = useMemo(() => dayjs(weekEnd).format("YYYY-MM-DD"), [weekEnd]);
  // Widen the actual fetch by ±1 calendar day so overnight punches at
  // start_qhnum>0 stores are captured. We trim back to the requested
  // business week below using each row's business_date field. For
  // start_qhnum=0 stores this is a no-op — business_date equals date_start
  // for every row.
  const actualFetchStart = useMemo(
    () => dayjs(weekStart).subtract(1, "day").format("YYYY-MM-DD"),
    [weekStart]
  );
  const actualFetchEnd = useMemo(
    () => dayjs(weekEnd).add(1, "day").format("YYYY-MM-DD"),
    [weekEnd]
  );

  const [schedule, setSchedule] = useState([]);
  const [actual, setActual] = useState([]);
  // Payroll-grade wage data from get_effective_wages — fetched across the
  // full week so shifts that fall in a different pay period later in the
  // week (semi-monthly pay periods, etc.) cost at the right rate. Shape:
  // { effective_wages_by_pay_period, pay_period_by_date }.
  const [wageData, setWageData] = useState(null);
  const [roleTypeMap, setRoleTypeMap] = useState(new Map());
  const [actualSalesByDate, setActualSalesByDate] = useState(new Map());
  const [forecastByDate, setForecastByDate] = useState(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!homeStore) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      const results = await Promise.allSettled([
        getRightworkAPI(API_get_schedule, {
          store_id: homeStore,
          lower_bound_date: startStr,
          upper_bound_date: endStr,
          should_return_all_stores: false,
        }),
        fetchStoreScopedRange(
          API_get_actual_used_schedule,
          homeStore,
          actualFetchStart,
          actualFetchEnd
        ),
        getRightworkAPI(API_get_effective_wages, {
          store_id: homeStore,
          date_start: startStr,
          date_end: endStr,
        }),
        fetchStoreScoped(API_get_role_types, homeStore),
        fetchStoreScopedRange(
          API_get_actual_sales_daily,
          homeStore,
          startStr,
          endStr
        ),
        fetchStoreScopedRange(
          API_get_forecasted_sales_daily,
          homeStore,
          startStr,
          endStr
        ),
      ]);

      if (cancelled) return;

      const [
        scheduleRes,
        actualRes,
        wagesRes,
        roleTypesRes,
        actualSalesRes,
        forecastRes,
      ] = results;

      if (scheduleRes.status === "rejected") {
        setError("Couldn't load the schedule. Try again.");
        setIsLoading(false);
        return;
      }

      const schedulePayload = unwrapPayload(scheduleRes.value);
      const scheduleList = Array.isArray(schedulePayload?.schedule)
        ? schedulePayload.schedule
        : Array.isArray(schedulePayload)
        ? schedulePayload
        : [];
      setSchedule(scheduleList);

      const actualList =
        actualRes.status === "fulfilled" && Array.isArray(actualRes.value?.data)
          ? actualRes.value.data
          : [];
      setActual(actualList);

      // Payroll-grade wage data — non-fatal if it fails.
      setWageData(wagesRes.status === "fulfilled" ? wagesRes.value?.data ?? null : null);

      // role_type id → name lookup
      const roleTypeMapNext = new Map();
      if (roleTypesRes.status === "fulfilled") {
        const payload = unwrapPayload(roleTypesRes.value);
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.role_types)
          ? payload.role_types
          : [];
        for (const rt of list) {
          if (rt?.id != null) {
            roleTypeMapNext.set(rt.id, rt.role_type || rt.name || "—");
          }
        }
      }
      setRoleTypeMap(roleTypeMapNext);

      setActualSalesByDate(
        actualSalesRes.status === "fulfilled"
          ? indexByDate(unwrapPayload(actualSalesRes.value))
          : new Map()
      );
      setForecastByDate(
        forecastRes.status === "fulfilled"
          ? indexByDate(unwrapPayload(forecastRes.value))
          : new Map()
      );

      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, startStr, endStr, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // Payroll-grade wage resolver: role-specific override first, falls back to
  // the employee's general wage. Returns 0 when no hourly record exists so
  // wage totals remain numeric. Matches the Paylocity export's precedence.
  // Per-date: a week that straddles a pay-period boundary costs each day at
  // the correct period's rate (semi-monthly pay periods within a weekly
  // business week is the common case).
  const wageFor = useCallback(
    (stmId, scheduledRoleId, dateStr) => {
      const w = lookupEffectiveWageOnDate(
        wageData,
        dateStr,
        stmId,
        scheduledRoleId
      );
      if (w == null) return 0;
      const n = Number(w);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    [wageData]
  );

  // Big aggregator: in one pass over the data, produce buckets for
  // employee, job, work center, and per-day, plus an overall week total.
  const aggregates = useMemo(() => {
    const byEmployee = new Map();
    const byJob = new Map();
    const byWorkCenter = new Map();
    const byDate = new Map();
    const total = emptyBucket();

    // Seed the per-date buckets so days with no activity still render.
    for (const d of weekDates) {
      const dateStr = dayjs(d).format("YYYY-MM-DD");
      byDate.set(dateStr, { key: dateStr, ...emptyBucket() });
    }

    const ensure = (map, key, label) => {
      if (!map.has(key)) {
        map.set(key, { key, label: label ?? key, ...emptyBucket() });
      }
      return map.get(key);
    };

    // Scheduled side
    for (const s of schedule) {
      const stm = s.store_team_member;
      if (!stm) continue;
      const hours = Number(s.hours ?? s.shift_length ?? 0);
      if (Number.isNaN(hours)) continue;
      const scheduledRoleId =
        typeof s.role_id === "object" ? s.role_id?.id ?? null : s.role_id ?? null;
      const wage = wageFor(stm.id, scheduledRoleId, s.date);
      const wages = wage * hours;
      const jobName = s.role_id?.role_name || "Unassigned";
      const wcName =
        roleTypeMap.get(s.role_id?.role_type) ||
        roleTypeMap.get(s.role_id?.role_type_id) ||
        "—";
      const dateKey = s.date;

      const emp = ensure(byEmployee, stm.id, displayName(stm));
      emp.scheduled.shifts += 1;
      emp.scheduled.hours += hours;
      emp.scheduled.wages += wages;

      const job = ensure(byJob, jobName, jobName);
      job.scheduled.shifts += 1;
      job.scheduled.hours += hours;
      job.scheduled.wages += wages;

      const wc = ensure(byWorkCenter, wcName, wcName);
      wc.scheduled.shifts += 1;
      wc.scheduled.hours += hours;
      wc.scheduled.wages += wages;

      if (byDate.has(dateKey)) {
        const day = byDate.get(dateKey);
        day.scheduled.shifts += 1;
        day.scheduled.hours += hours;
        day.scheduled.wages += wages;
      }

      total.scheduled.shifts += 1;
      total.scheduled.hours += hours;
      total.scheduled.wages += wages;
    }

    // Actual side
    for (const a of actual) {
      // Skip no-show placeholder rows — see Daily version for rationale.
      if (isNoShowActualShift(a)) continue;
      // Trim the ±1 day over-fetch back to the requested business week
      // and use business_date as the per-day bucket key. For stores with
      // start_qhnum=0 this is a no-op (business_date == date_start).
      const aBusinessDate = a.business_date || a.date_start;
      if (
        aBusinessDate == null ||
        aBusinessDate < startStr ||
        aBusinessDate > endStr
      ) {
        continue;
      }
      const stm =
        a.store_team_member || a.published?.store_team_member || null;
      const stmId = stm?.id ?? a.published?.store_team_member?.id ?? null;
      const hours =
        shiftDurationHours(a.shift_start, a.shift_end, a.total_break) ?? 0;
      // Prefer the role the employee actually clocked into; fall back to
      // the scheduled role. See the Daily version for the rationale —
      // wage uses actual role, job grouping stays on scheduled.
      const rawScheduledRoleId = a.published?.role_id;
      const scheduledRoleId =
        typeof rawScheduledRoleId === "object"
          ? rawScheduledRoleId?.id ?? null
          : rawScheduledRoleId ?? null;
      const wageRoleId = a.actual_role_id ?? scheduledRoleId;
      const wage = wageFor(stmId, wageRoleId, aBusinessDate);
      const wages = wage * hours;
      const jobName = a.published?.role_id?.role_name || "Unassigned";
      const wcName =
        roleTypeMap.get(a.published?.role_id?.role_type) ||
        roleTypeMap.get(a.published?.role_id?.role_type_id) ||
        "—";
      const dateKey = aBusinessDate;

      if (stmId != null) {
        const emp = ensure(
          byEmployee,
          stmId,
          stm ? displayName(stm) : "—"
        );
        emp.actual.shifts += 1;
        emp.actual.hours += hours;
        emp.actual.wages += wages;
      }

      const job = ensure(byJob, jobName, jobName);
      job.actual.shifts += 1;
      job.actual.hours += hours;
      job.actual.wages += wages;

      const wc = ensure(byWorkCenter, wcName, wcName);
      wc.actual.shifts += 1;
      wc.actual.hours += hours;
      wc.actual.wages += wages;

      if (byDate.has(dateKey)) {
        const day = byDate.get(dateKey);
        day.actual.shifts += 1;
        day.actual.hours += hours;
        day.actual.wages += wages;
      }

      total.actual.shifts += 1;
      total.actual.hours += hours;
      total.actual.wages += wages;
    }

    return {
      employees: Array.from(byEmployee.values()).sort((x, y) =>
        x.label.localeCompare(y.label)
      ),
      jobs: Array.from(byJob.values()).sort((x, y) =>
        x.label.localeCompare(y.label)
      ),
      workCenters: Array.from(byWorkCenter.values()).sort((x, y) =>
        x.label.localeCompare(y.label)
      ),
      dailyBuckets: weekDates.map((d) => byDate.get(dayjs(d).format("YYYY-MM-DD"))),
      total,
    };
  }, [schedule, actual, wageFor, roleTypeMap, weekDates]);

  const filters = (
    <div className="flex items-center">
      <WeekSelector
        start_date={weekStart}
        end_date={weekEnd}
        increment_week={incrementWeek}
        set_week_start_date={setWeekStartDate}
        businessWeekStartDayIndex={weekStartDayIndex}
      />
    </div>
  );

  const weekLabel = `${dayjs(weekStart).format("MMM D")} – ${dayjs(
    weekEnd
  ).format("MMM D, YYYY")}`;

  const hasAnyData =
    aggregates.employees.length > 0 || aggregates.total.actual.shifts > 0;

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : !hasAnyData ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No scheduled or actual shifts this week.
        </div>
      ) : (
        <>
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-slate-700">{weekLabel}</h4>
            <p className="text-xs text-slate-500">
              Scheduled vs. actual hours and wages for the week, summarized
              four ways. Wages are estimates at each employee's effective
              hourly rate (no OT/DT multipliers applied). For payroll-grade
              per-shift pay including overtime and premiums, use the Daily
              Punch Audit.
            </p>
          </div>

          <SectionCard title="Employee Summary">
            <SummaryTable rows={aggregates.employees} labelHeader="Employee" />
          </SectionCard>

          <SectionCard title="Job Summary">
            <SummaryTable rows={aggregates.jobs} labelHeader="Job" />
          </SectionCard>

          <SectionCard
            title="Work Center Summary"
            footnote={
              roleTypeMap.size === 0
                ? "Work center names couldn't be resolved — showing role-type ids."
                : null
            }
          >
            <SummaryTable
              rows={aggregates.workCenters}
              labelHeader="Work Center"
            />
          </SectionCard>

          <SectionCard
            title="Day of Business Summary"
            footnote="Per-day scheduled/actual totals with projected vs. actual sales and labor %, plus a weekly total."
          >
            <table className="w-full text-xs">
              <thead className="bg-white border-b border-slate-100">
                <tr>
                  <TH>Day</TH>
                  <TH align="right">Sched Shifts</TH>
                  <TH align="right">Act Shifts</TH>
                  <TH align="right">Sched Hrs</TH>
                  <TH align="right">Act Hrs</TH>
                  <TH align="right">Proj Wages</TH>
                  <TH align="right">Act Wages</TH>
                  <TH align="right">Proj Sales</TH>
                  <TH align="right">Act Sales</TH>
                  <TH align="right">Proj Labor %</TH>
                  <TH align="right">Act Labor %</TH>
                </tr>
              </thead>
              <tbody>
                {aggregates.dailyBuckets.map((day, i) => {
                  const date = weekDates[i];
                  const projSales = forecastByDate.get(day.key) ?? null;
                  const actSales = actualSalesByDate.get(day.key) ?? null;
                  return (
                    <tr
                      key={day.key}
                      className="border-b border-slate-50 hover:bg-slate-50"
                    >
                      <TD strong>
                        <div>{DAY_NAMES[date.getDay()]}</div>
                        <div className="text-[10px] text-slate-500 font-normal">
                          {dayjs(date).format("M/D/YYYY")}
                        </div>
                      </TD>
                      <TD align="right">{day.scheduled.shifts}</TD>
                      <TD align="right">{day.actual.shifts}</TD>
                      <TD align="right">{formatHours(day.scheduled.hours)}</TD>
                      <TD align="right">{formatHours(day.actual.hours)}</TD>
                      <TD align="right">
                        {formatCurrency(day.scheduled.wages)}
                      </TD>
                      <TD align="right">{formatCurrency(day.actual.wages)}</TD>
                      <TD align="right">
                        {projSales == null ? "—" : formatCurrency(projSales)}
                      </TD>
                      <TD align="right">
                        {actSales == null ? "—" : formatCurrency(actSales)}
                      </TD>
                      <TD align="right">
                        {laborPct(day.scheduled.wages, projSales) == null
                          ? "—"
                          : `${laborPct(day.scheduled.wages, projSales).toFixed(
                              1
                            )}%`}
                      </TD>
                      <TD align="right">
                        {laborPct(day.actual.wages, actSales) == null
                          ? "—"
                          : `${laborPct(day.actual.wages, actSales).toFixed(
                              1
                            )}%`}
                      </TD>
                    </tr>
                  );
                })}
                {(() => {
                  const projSalesTotal = Array.from(
                    forecastByDate.values()
                  ).reduce((s, v) => s + (Number(v) || 0), 0);
                  const actSalesTotal = Array.from(
                    actualSalesByDate.values()
                  ).reduce((s, v) => s + (Number(v) || 0), 0);
                  return (
                    <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold">
                      <TD strong>Week total</TD>
                      <TD align="right" strong>
                        {aggregates.total.scheduled.shifts}
                      </TD>
                      <TD align="right" strong>
                        {aggregates.total.actual.shifts}
                      </TD>
                      <TD align="right" strong>
                        {formatHours(aggregates.total.scheduled.hours)}
                      </TD>
                      <TD align="right" strong>
                        {formatHours(aggregates.total.actual.hours)}
                      </TD>
                      <TD align="right" strong>
                        {formatCurrency(aggregates.total.scheduled.wages)}
                      </TD>
                      <TD align="right" strong>
                        {formatCurrency(aggregates.total.actual.wages)}
                      </TD>
                      <TD align="right" strong>
                        {projSalesTotal === 0
                          ? "—"
                          : formatCurrency(projSalesTotal)}
                      </TD>
                      <TD align="right" strong>
                        {actSalesTotal === 0
                          ? "—"
                          : formatCurrency(actSalesTotal)}
                      </TD>
                      <TD align="right" strong>
                        {laborPct(
                          aggregates.total.scheduled.wages,
                          projSalesTotal
                        ) == null
                          ? "—"
                          : `${laborPct(
                              aggregates.total.scheduled.wages,
                              projSalesTotal
                            ).toFixed(1)}%`}
                      </TD>
                      <TD align="right" strong>
                        {laborPct(
                          aggregates.total.actual.wages,
                          actSalesTotal
                        ) == null
                          ? "—"
                          : `${laborPct(
                              aggregates.total.actual.wages,
                              actSalesTotal
                            ).toFixed(1)}%`}
                      </TD>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </SectionCard>
        </>
      )}
    </PrebuiltReportShell>
  );
}
